import { CSSProperties, forwardRef, HTMLAttributes } from 'react';
import { flexRender, Row } from '@tanstack/react-table';
import styled from 'styled-components';
import {
  colorBgContainer,
  colorBgLayout,
  components,
  fontSize,
  lineHeight,
  lineHeightPx,
} from '@atlas-frontend/design-tokens';
import { cleanArray } from '../../utils';
import { stickyStyle } from './stickyStyle';

export type TableRowProps = {
  index: number;
  row: Row<any>;
  style?: CSSProperties;
  htmlAttributes?: HTMLAttributes<HTMLTableRowElement>;
};

export const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(
  (props, ref) => {
    const { row, style, htmlAttributes } = props;
    const visibleCells = row.getVisibleCells();

    return (
      <tr ref={ref} style={style} {...htmlAttributes}>
        {visibleCells.map((cell, idx) => (
          <TableCell
            key={`${cell.id}-${idx}`}
            $dark={!!cell.column.getIsSorted()}
            style={{
              textAlign: cell.column.columnDef.meta?.textAlign,
            }}
            className={cleanArray([
              (cell.column.columnDef as any).accessorKey,
              cell.column.columnDef.meta?.className,
              cell.column.columnDef.meta?.cellClassName,
            ])?.join(' ')}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </tr>
    );
  },
);

type TableCellProps = {
  $dark?: boolean;
  $isExpandedContentCell?: boolean;
};

export const TableCell = styled.td<TableCellProps>`
  box-sizing: content-box;
  height: ${lineHeightPx}px;
  background-color: ${(props) =>
    props.$dark ? colorBgLayout : colorBgContainer};

  &,
  .ant-typography {
    font-size: ${fontSize}px;
    line-height: ${lineHeight};
  }

  ${({ $isExpandedContentCell }) =>
    $isExpandedContentCell
      ? `
  padding: 0;`
      : `
  padding-block: ${components.Table.cellPaddingBlockSM}px;
  padding-inline: ${components.Table.cellPaddingInlineSM}px;`}

  vertical-align: top;
  position: relative;
  border-bottom: 1px solid ${components.Table.borderColor};

  tr.has-expanded-row & {
    box-shadow: none;
  }

  ${stickyStyle}

  &.drag {
    background-color: ${colorBgLayout};
    box-shadow: none;
  }
`;
